/**
 * Calendar date math for `DatePickerCalendar`, on plain `YYYY-MM-DD` strings
 * (a calendar date, no zone: the caller's dates are already in the zone they
 * belong to). All arithmetic is done in UTC so the viewer's own zone and DST
 * never shift a day. Weeks start on Monday.
 */

const YMD = /^(\d{4})-(\d{2})-(\d{2})$/;

const toUtc = (ymd: string): Date | null => {
	const m = YMD.exec(ymd);
	if (!m) return null;
	const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
	return d.getUTCFullYear() === Number(m[1]) && d.getUTCMonth() === Number(m[2]) - 1 && d.getUTCDate() === Number(m[3]) ? d : null;
};

const p2 = (n: number) => String(n).padStart(2, "0");
const fromUtc = (d: Date): string => `${d.getUTCFullYear()}-${p2(d.getUTCMonth() + 1)}-${p2(d.getUTCDate())}`;

export const isYmd = (v: string): boolean => toUtc(v) !== null;

/** A `YYYY-MM-DD` as a UTC-midnight Date (for Intl with `timeZone: "UTC"`). */
export const ymdToUtcDate = (ymd: string): Date => toUtc(ymd) ?? new Date(NaN);

/** The month a date sits in: `YYYY-MM`. */
export const monthOf = (ymd: string): string => ymd.slice(0, 7);

export function addDays(ymd: string, n: number): string {
	const d = toUtc(ymd);
	if (!d) return ymd;
	d.setUTCDate(d.getUTCDate() + n);
	return fromUtc(d);
}

/** Same day `n` months on, clamped to the month's last day (31 Jan + 1 → 28/29 Feb). */
export function addMonths(ymd: string, n: number): string {
	const d = toUtc(ymd);
	if (!d) return ymd;
	const day = d.getUTCDate();
	const first = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + n, 1));
	const last = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
	first.setUTCDate(Math.min(day, last));
	return fromUtc(first);
}

/** 0 = Monday … 6 = Sunday. */
export const weekdayMonday = (ymd: string): number => ((toUtc(ymd)?.getUTCDay() ?? 1) + 6) % 7;

export const startOfWeek = (ymd: string): string => addDays(ymd, -weekdayMonday(ymd));
export const endOfWeek = (ymd: string): string => addDays(ymd, 6 - weekdayMonday(ymd));

/**
 * The month `YYYY-MM` as weeks of seven, Monday first; days outside the
 * month are null (the grid shows only this month's days).
 */
export function monthGrid(month: string): (string | null)[][] {
	const first = `${month}-01`;
	const start = toUtc(first);
	if (!start) return [];
	const days = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 0)).getUTCDate();
	const cells: (string | null)[] = Array.from({ length: weekdayMonday(first) }, () => null);
	for (let i = 1; i <= days; i++) cells.push(`${month}-${p2(i)}`);
	while (cells.length % 7 !== 0) cells.push(null);
	const weeks: (string | null)[][] = [];
	for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
	return weeks;
}

/** Monday … Sunday of some week, as UTC Dates, for weekday names via Intl. */
export const MONDAY_WEEK: readonly Date[] = Array.from({ length: 7 }, (_, i) => new Date(Date.UTC(2024, 0, 1 + i))); // 2024-01-01 was a Monday

/**
 * Where keyboard focus starts: the selected day, else the first enabled day
 * on or after `today`, else the first enabled day, else `today`.
 */
export function initialFocus(selected: string | null, enabled: readonly string[] | null, today: string): string {
	if (selected && isYmd(selected)) return selected;
	if (enabled && enabled.length) return enabled.find((d) => d >= today) ?? enabled[0]!;
	return today;
}

/**
 * The key → the day focus moves to (APG date picker grid): arrows ±1 day /
 * ±1 week, Home / End the week's Monday / Sunday, PageUp / PageDown ±1 month
 * (with Shift ±1 year). Null for any other key.
 */
export function moveFocus(ymd: string, key: string, shift = false): string | null {
	switch (key) {
		case "ArrowLeft":
			return addDays(ymd, -1);
		case "ArrowRight":
			return addDays(ymd, 1);
		case "ArrowUp":
			return addDays(ymd, -7);
		case "ArrowDown":
			return addDays(ymd, 7);
		case "Home":
			return startOfWeek(ymd);
		case "End":
			return endOfWeek(ymd);
		case "PageUp":
			return addMonths(ymd, shift ? -12 : -1);
		case "PageDown":
			return addMonths(ymd, shift ? 12 : 1);
		default:
			return null;
	}
}

/** Keeps a day inside [min, max] (either end optional). */
export function clampDay(ymd: string, min?: string | null, max?: string | null): string {
	if (min && ymd < min) return min;
	if (max && ymd > max) return max;
	return ymd;
}
